import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { trackLabel, type StudioNowAction, type StudioSectionRole, type StudioTrack } from "@/lib/studioVoice";
import type { StudioTab } from "@/lib/carouselStudioMeta";

const ROLE_LABEL: Record<StudioSectionRole, string | null> = {
  next: "Teraz",
  path: null,
  optional: "Opcjonalne",
  info: null,
};

export function StudioTrackChip({
  track,
  className,
}: {
  track: StudioTrack;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
        track === "operator" && "border-slate-300 bg-slate-50 text-slate-800",
        track === "ai" && "border-brand/40 bg-brand/10 text-brand",
        !track && "border-amber-300 bg-amber-50 text-amber-950",
        className,
      )}
    >
      {trackLabel(track)}
    </span>
  );
}

export function StudioNowBar({
  action,
  currentTab,
  onGo,
  className,
}: {
  action: StudioNowAction;
  currentTab: StudioTab;
  onGo: (tab: StudioTab, sectionId?: string) => void;
  className?: string;
}) {
  const sameTab = action.tab === currentTab;
  const canJump = !sameTab || Boolean(action.sectionId);
  return (
    <div
      id="studio-now"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand/30 bg-brand/5 px-3 py-2.5",
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-brand">Teraz</p>
        <p className="text-sm font-medium text-slate-900">{action.label}</p>
        <p className="text-xs text-muted-foreground">{action.detail}</p>
      </div>
      {canJump ? (
        <Button
          type="button"
          size="sm"
          variant="brand"
          className="shrink-0"
          onClick={() => {
            onGo(action.tab, action.sectionId);
            if (sameTab && action.sectionId && typeof document !== "undefined") {
              window.setTimeout(() => {
                document.getElementById(action.sectionId!)?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }
          }}
        >
          {sameTab ? "Pokaż" : "Idź"}
        </Button>
      ) : null}
    </div>
  );
}

export function StudioSection({
  role,
  title,
  hint,
  id,
  defaultOpen,
  children,
  className,
  actions,
}: {
  role: StudioSectionRole;
  title?: string;
  hint?: string;
  id?: string;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
}) {
  const badge = ROLE_LABEL[role];

  if (role === "info") {
    return (
      <div id={id} className={cn("space-y-1 text-xs text-muted-foreground", className)}>
        {title ? <p className="text-xs font-medium text-slate-600">{title}</p> : null}
        {hint ? <p>{hint}</p> : null}
        {children}
      </div>
    );
  }

  if (role === "optional") {
    return (
      <details
        id={id}
        className={cn(
          "group rounded-md border border-dashed border-slate-300 bg-slate-50/60 open:bg-white",
          className,
        )}
        open={defaultOpen}
      >
        <summary className="cursor-pointer list-none px-3 py-2.5 marker:content-none [&::-webkit-details-marker]:hidden">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              Opcjonalne
            </span>
            {title ? <span className="text-sm font-medium text-slate-800">{title}</span> : null}
          </div>
          {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
        </summary>
        <div className="space-y-3 border-t border-slate-200 px-3 py-3">{children}</div>
      </details>
    );
  }

  if (role === "next") {
    return (
      <section
        id={id}
        className={cn(
          "space-y-3 rounded-lg border border-brand/35 bg-white p-3 shadow-sm ring-1 ring-brand/10",
          className,
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {badge ? (
              <span className="mb-1 inline-block rounded bg-brand/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
                {badge}
              </span>
            ) : null}
            {title ? <h3 className="text-sm font-semibold text-slate-900">{title}</h3> : null}
            {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
          </div>
          {actions}
        </div>
        {children}
      </section>
    );
  }

  return (
    <section id={id} className={cn("space-y-3 rounded-md border border-slate-200 bg-white p-3", className)}>
      {(title || hint || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {title ? <h3 className="text-sm font-medium text-slate-900">{title}</h3> : null}
            {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function StudioInfoStrip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-wrap gap-2 text-xs text-muted-foreground", className)}>{children}</div>
  );
}
